/**
 * PWA install prompt banner — appears on the customer app hostname when the
 * browser fires `beforeinstallprompt`. Dismissable and remembers the choice.
 */
import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "cyn_pwa_dismissed_at";
const DISMISS_TTL = 1000 * 60 * 60 * 24 * 7; // 7 days

export default function PWAInstallBanner() {
  const [prompt, setPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Respect a recent dismissal
    try {
      const dismissedAt = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
      if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL) return;
    } catch (_) {}

    const onPrompt = (e) => {
      e.preventDefault();
      setPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  if (!visible) return null;

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    await prompt.userChoice;
    setVisible(false);
  };
  const dismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
    setVisible(false);
  };

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50 rounded-2xl bg-slate-900 text-white shadow-2xl p-4 flex items-start gap-3"
      data-testid="pwa-install-banner"
    >
      <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center shrink-0">
        <Download className="w-5 h-5 text-cyan-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">Install Cyannect</div>
        <div className="text-xs text-slate-400 mt-0.5">Add to your home screen — coupons, cashback and wallet, one tap away.</div>
        <div className="flex gap-2 mt-3">
          <button
            onClick={install}
            className="px-3 py-1.5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 text-xs font-semibold"
            data-testid="pwa-install-accept"
          >
            Install
          </button>
          <button
            onClick={dismiss}
            className="px-3 py-1.5 rounded-full text-xs text-slate-400 hover:text-white"
            data-testid="pwa-install-dismiss"
          >
            Not now
          </button>
        </div>
      </div>
      <button onClick={dismiss} className="text-slate-500 hover:text-white shrink-0" aria-label="Dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
